import { WarningAmberOutlined } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type CSSProperties, type FC, Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { SystemTabs } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import { StaffAutoComplete } from "../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemAutoComplete } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { type DropdownItem, SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import { CredentialStatus, type LocationCredentialResponse, type Nurse } from "../../utils/models";
import { useCredentialsMutationHooks, useLocationCredentialsQueryHook } from "../Profile/v2/SynergyCredentials/credentialsHooks";
import { NewCredentialPopover } from "../Profile/v2/SynergyCredentials/NewCredentialPopover";
import { SynergyFacilityOptionStartAdornment } from "../Profile/v2/SynergyCredentials/SynergyFacilityOptionStartAdornment";
import { EditFacilityPageAddress } from "./EditFacilityPageAddress";
import { FacilityScheduleGrid } from "./FacilityScheduleGrid/FacilityScheduleGrid";
import { type FacilityCredentialSortKey, useFacilityPageState } from "./useFacilityPageState";

const gridCellStyle = {
	background: "transparent",
	border: "1px solid rgb(236, 239, 241)",
	fontWeight: 400,
	fontSize: "14px",
	lineHeight: "16px",
	padding: "6px 10px",
	color: "var(--inhouse-primary-dark)",
	height: 44,
	display: "flex",
	alignItems: "center",
	justifyContent: "start",
	gap: 1,
} as const satisfies CSSProperties;

const spanStyle = {
	maxWidth: "100%",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
} as const satisfies CSSProperties;

const stickyHeaderStyle = {
	border: "1px solid rgb(236, 239, 241)",
	fontWeight: 600,
	fontSize: "14px",
	lineHeight: "100%",
	padding: "6px 10px",
	color: "var(--inhouse-primary-dark)",
	position: "sticky",
	top: 0,
	zIndex: 2,
	background: "var(--inhouse-primary-alpha-08)",
	display: "flex",
	alignItems: "center",
	gap: 1,
} as const satisfies CSSProperties;

const buttonHeaderStyle = {
	...stickyHeaderStyle,
	cursor: "pointer",
	justifyContent: "space-between",
} as const satisfies CSSProperties;

const SortButton = ({
	buttonKey,
	currentSortingKey,
	direction,
	onClick,
}: {
	buttonKey: FacilityCredentialSortKey;
	currentSortingKey: FacilityCredentialSortKey;
	direction: "asc" | "desc";
	onClick: (newSortingKey: FacilityCredentialSortKey, direction: "asc" | "desc") => void;
}) => {
	return (
		<IconButton
			onClick={() => onClick(buttonKey, buttonKey !== currentSortingKey ? "desc" : direction === "asc" ? "desc" : "asc")}
			size="small"
			sx={{
				opacity: currentSortingKey === buttonKey ? 1 : 0.3,
				transition: "opacity 0.3s ease",
			}}
		>
			{direction === "desc" || buttonKey !== currentSortingKey ? <ArrowDownwardIcon fontSize="inherit" /> : <ArrowUpwardIcon fontSize="inherit" />}
		</IconButton>
	);
};

type FacilityTab = "credentials" | "schedule";

const FACILITY_TABS = [
	{ label: "Credentials", value: "credentials" as FacilityTab },
	{ label: "Schedule", value: "schedule" as FacilityTab },
];

export const FacilityPage: FC = () => {
	const { showErrorSnackbar, hospitalData, chosenDepartment } = useHospitalData();
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();

	const {
		selectedLocation,
		handleLocationChange,
		autocompleteOptions,
		isPopoverOpen,
		handleTogglePopover,
		selectedCredentialForDelete,
		showDeleteConfirmation,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		sortKey,
		sortDirection,
		handleSort,
		formattedNurseFullName,
	} = useFacilityPageState();

	const [activeTab, setActiveTab] = useState<FacilityTab>("credentials");
	const [selectedStaff, setSelectedStaff] = useState<SystemAutoCompleteOption<Nurse> | null>(null);

	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			hospitalData.nurses
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				.map((nurse) => ({
					label: formatPersonName(nurse),
					value: nurse,
				})),
		[hospitalData.nurses, chosenDepartment.department_id, formatPersonName],
	);

	const { createCredential, isCreatingCredential, deleteCredential, isDeletingCredential } = useCredentialsMutationHooks();

	const { credentials: rawCredentials, isLoading } = useLocationCredentialsQueryHook({
		locationId: selectedLocation?.value.id,
		hideExpired: false,
	});

	const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);
	const [editedStatus, setEditedStatus] = useState<CredentialStatus | null>(null);

	// Sort credentials
	const sortedCredentials = useMemo(() => {
		return rawCredentials.slice().sort((a, b) => {
			let compareValue = 0;

			switch (sortKey) {
				case "id":
					compareValue = a.id.localeCompare(b.id);
					break;
				case "nurse_id":
					compareValue = a.nurse_id - b.nurse_id;
					break;
				case "staff_name":
					// Sorts by the name as the column shows it, so the order follows the department's name-order flag.
					compareValue = formattedNurseFullName(a).toLowerCase().localeCompare(formattedNurseFullName(b).toLowerCase());
					break;
				case "created_at":
					compareValue = a.created_at.toMillis() - b.created_at.toMillis();
					break;
				case "expires_at": {
					const dateA = a.expires_at ? a.expires_at.toMillis() : Infinity;
					const dateB = b.expires_at ? b.expires_at.toMillis() : Infinity;
					compareValue = dateA - dateB;
					break;
				}
				case "status":
					compareValue = a.status.localeCompare(b.status);
					break;
			}

			return sortDirection === "asc" ? compareValue : -compareValue;
		});
	}, [rawCredentials, sortKey, sortDirection, formattedNurseFullName]);

	// Filter by selected staff member
	const credentials = useMemo(() => {
		if (!selectedStaff) return sortedCredentials;
		return sortedCredentials.filter((c) => c.nurse_id === selectedStaff.value.id);
	}, [sortedCredentials, selectedStaff]);

	const handleNewCredentialSubmit = useCallback(
		async (locationId: string, expiresAt: string | null, nurseIds: number[], status: CredentialStatus) => {
			if (!nurseIds || nurseIds.length === 0) {
				showErrorSnackbar("Please select at least one nurse for the credential.", { nurseIds });
				return;
			}
			await createCredential({
				request: {
					nurse_ids: nurseIds,
					location_id: locationId,
					expires_at: expiresAt,
					status,
				},
				locationIdForInvalidation: selectedLocation?.value.id,
			});
			handleTogglePopover();
		},
		[createCredential, handleTogglePopover, showErrorSnackbar, selectedLocation?.value.id],
	);

	const handleDeleteConfirmClick = useCallback(async () => {
		if (selectedCredentialForDelete) {
			await deleteCredential({
				credentialId: selectedCredentialForDelete,
				locationIdForInvalidation: selectedLocation?.value.id,
			});
			handleDeleteConfirm();
		}
	}, [selectedCredentialForDelete, deleteCredential, handleDeleteConfirm, selectedLocation?.value.id]);

	const handleEditClick = useCallback((credential: LocationCredentialResponse) => {
		setEditingCredentialId(credential.id);
		setEditedStatus(credential.status);
	}, []);

	const handleEditCancel = useCallback(() => {
		setEditingCredentialId(null);
		setEditedStatus(null);
	}, []);

	const handleEditSave = useCallback(
		async (credential: LocationCredentialResponse) => {
			if (!editedStatus) return;

			await createCredential({
				request: {
					nurse_ids: [credential.nurse_id],
					location_id: credential.department_location_id,
					expires_at: credential.expires_at?.toISO() ?? null,
					status: editedStatus,
				},
				locationIdForInvalidation: selectedLocation?.value.id,
			});
			setEditingCredentialId(null);
			setEditedStatus(null);
		},
		[editedStatus, createCredential, selectedLocation?.value.id],
	);

	const isCredentialExpired = useCallback((credential: LocationCredentialResponse): boolean => {
		if (!credential.expires_at) return false;
		const expiresAt = credential.expires_at;
		const today = DateTime.now().startOf("day");
		return expiresAt < today;
	}, []);

	const formatDate = useCallback((date: DateTime): string => {
		return date.toFormat("MMM d, yyyy");
	}, []);

	const statusDropdownItems = useMemo<DropdownItem<CredentialStatus>[]>(() => {
		return Object.values(CredentialStatus).map((status) => ({
			value: status,
			label: status,
			id: status,
		}));
	}, []);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				gap: "24px",
				padding: "24px",
			}}
		>
			<Typography
				variant="h5"
				sx={{
					fontWeight: 600,
					color: "#1A1A1A",
				}}
			>
				{t("facilities.page.title", { defaultValue: "Facility Credentials" })}
			</Typography>
			<Box
				sx={{
					height: "calc(100% - 72px)",
					display: "flex",
					flexDirection: "column",
					gap: "16px",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						width: "100%",
						gap: "16px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "start",
							gap: 2,
						}}
					>
						<SystemAutoComplete
							onChange={handleLocationChange}
							value={selectedLocation}
							options={autocompleteOptions}
							textFieldProps={{
								placeholder: t("facilities.location.placeholder", { defaultValue: "Type Facility name/type or Section" }),
								label: t("facilities.location.label", { defaultValue: "Select Facility" }),
								reserveHeightForHelperText: false,
								inputSx: {
									width: "400px",
								},
							}}
							getOptionStartAdornment={(option) => <SynergyFacilityOptionStartAdornment facilityType={option.value.departmentType} />}
							justifyContent={"start"}
						/>
						{selectedLocation && <EditFacilityPageAddress location={selectedLocation.value} />}
					</Box>
					{selectedLocation && (
						<SystemReactTinyPopover
							isOpen={isPopoverOpen}
							positions={["bottom", "top"]}
							align="end"
							content={
								<NewCredentialPopover
									onSubmit={handleNewCredentialSubmit}
									onCancel={handleTogglePopover}
									isSubmitting={isCreatingCredential}
									defaultLocationId={selectedLocation.value.id}
									nurseSelectionMode={"multiple"}
									credentials={sortedCredentials}
								/>
							}
						>
							<SystemButton buttonVariant="primary" onClick={handleTogglePopover} disabled={isLoading}>
								{t("credential.new.button", { defaultValue: "New" })}
							</SystemButton>
						</SystemReactTinyPopover>
					)}
				</Box>

				{selectedLocation && (
					<>
						<SystemTabs tabs={FACILITY_TABS} currentTab={activeTab} onTabChange={setActiveTab} />

						{activeTab === "credentials" && (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: "12px",
								}}
							>
								<Box>
									<StaffAutoComplete nurses={nurseOptions} value={selectedStaff} onChange={(_, val) => setSelectedStaff(val)} width="280px" />
								</Box>

								{isLoading ? (
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											padding: "40px",
										}}
									>
										<CircularProgress size={32} />
									</Box>
								) : credentials.length === 0 ? (
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											padding: "40px",
										}}
									>
										<Typography
											sx={{
												fontSize: "16px",
												color: "#999",
											}}
										>
											{t("facilities.credentials.empty", { defaultValue: "No credentials found for this facility" })}
										</Typography>
									</Box>
								) : (
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "80px 100px 2fr 1fr 1fr 300px 100px",
											width: "100%",
											maxHeight: "600px",
											overflow: "auto",
											border: "1px solid rgb(236, 239, 241)",
											borderRadius: "4px",
										}}
									>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.credential_id", { defaultValue: "Cred ID" })}
											<SortButton buttonKey="id" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.staff_id", { defaultValue: "Staff ID" })}
											<SortButton buttonKey="nurse_id" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.staff_name", { defaultValue: "Staff Full Name" })}
											<SortButton buttonKey="staff_name" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.created", { defaultValue: "Created" })}
											<SortButton buttonKey="created_at" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.expires", { defaultValue: "Expires" })}
											<SortButton buttonKey="expires_at" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={buttonHeaderStyle}>
											{t("facilities.table.status", { defaultValue: "Status" })}
											<SortButton buttonKey="status" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
										</Box>
										<Box sx={stickyHeaderStyle}>{t("facilities.table.actions", { defaultValue: "Actions" })}</Box>

										{credentials.map((credential) => {
											const isExpired = isCredentialExpired(credential);
											return (
												<Fragment key={credential.id}>
													<Box sx={gridCellStyle}>
														<span style={spanStyle}>{credential.id.substring(credential.id.length - 4).toUpperCase()}</span>
													</Box>
													<Box sx={gridCellStyle}>
														<span style={spanStyle}>{credential.nurse_id}</span>
													</Box>
													<Box sx={gridCellStyle}>
														<span style={spanStyle}>{formattedNurseFullName(credential)}</span>
													</Box>
													<Box sx={gridCellStyle}>
														<span style={spanStyle}>{formatDate(credential.created_at)}</span>
													</Box>
													<Box sx={gridCellStyle}>
														<span style={spanStyle}>
															{credential.expires_at ? formatDate(credential.expires_at) : t("credential.no_expiration", { defaultValue: "No Expiration" })}
														</span>
														{isExpired && (
															<InHouseTooltip title={"Credential Expired"} placement={"top"}>
																<WarningAmberOutlined fontSize={"inherit"} sx={{ color: "#DB3835" }} />
															</InHouseTooltip>
														)}
													</Box>
													<Box sx={gridCellStyle}>
														{editingCredentialId === credential.id ? (
															<SystemDropdown
																multiple={false}
																items={statusDropdownItems}
																selectedItem={editedStatus}
																onChange={(value) => setEditedStatus(value || null)}
																valueToString={(value) => value || ""}
																label=""
																fullWidth={true}
															/>
														) : (
															<InHouseTooltip title={credential.status} placement="top">
																<span style={spanStyle}>{credential.status}</span>
															</InHouseTooltip>
														)}
													</Box>
													<Box sx={gridCellStyle}>
														{editingCredentialId === credential.id ? (
															<Box
																sx={{
																	display: "flex",
																	gap: "4px",
																}}
															>
																<IconButton onClick={() => handleEditSave(credential)} size="small" disabled={isCreatingCredential}>
																	{isCreatingCredential ? <CircularProgress size={14} /> : <CheckIcon fontSize="inherit" />}
																</IconButton>
																<IconButton onClick={handleEditCancel} size="small" disabled={isCreatingCredential}>
																	<CloseIcon fontSize="inherit" />
																</IconButton>
															</Box>
														) : isDeletingCredential && selectedCredentialForDelete === credential.id ? (
															<CircularProgress size={gridCellStyle.fontSize} />
														) : (
															<Box
																sx={{
																	display: "flex",
																	gap: "4px",
																}}
															>
																<IconButton onClick={() => handleEditClick(credential)} size="small">
																	<EditIcon fontSize="inherit" />
																</IconButton>
																<IconButton onClick={() => handleDeleteClick(credential.id)} size="small">
																	<DeleteIcon fontSize="inherit" />
																</IconButton>
															</Box>
														)}
													</Box>
												</Fragment>
											);
										})}
									</Box>
								)}
							</Box>
						)}

						{activeTab === "schedule" && <FacilityScheduleGrid location={selectedLocation.value} />}
					</>
				)}

				{!selectedLocation && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							padding: "40px",
						}}
					>
						<Typography
							sx={{
								fontSize: "16px",
								color: "#999",
							}}
						>
							{t("facilities.select.prompt", { defaultValue: "Select a facility to view details" })}
						</Typography>
					</Box>
				)}
			</Box>
			{showDeleteConfirmation && (
				<SystemDialog
					open={showDeleteConfirmation}
					onClose={handleDeleteCancel}
					title={t("credential.delete.title", { defaultValue: "Delete Credential?" })}
					body={t("credential.delete.message", { defaultValue: "Are you sure you want to delete this credential? This action cannot be undone." })}
					onConfirm={handleDeleteConfirmClick}
					cancel={t("common.cancel", { defaultValue: "Cancel" })}
					confirm={t("common.delete", { defaultValue: "Delete" })}
				/>
			)}
		</Box>
	);
};
